import { Alert, Box, Link, Stack, Typography } from "@mui/material";
import { IssuesDataGrid, IssueWithUrl } from "components/IssuesDataGrid";
import { CREDENTIAL_REQUIRED_ACTIVITY_DESCRIPTION } from "encamp-shared/src/constants/tierii";
import { ActivityType, GetTierIiReportQuery } from "generated-graphql/graphql";

type Activity = NonNullable<
  GetTierIiReportQuery["tierIIReport"]["currentWorkflow"]
>["activities"][0];

type Facility = GetTierIiReportQuery["tierIIReport"]["facility"];
type Tenant = GetTierIiReportQuery["tierIIReport"]["tenant"];

/** Type for presubmission job output */
type PresubmissionOutput = {
  issues?: IssueWithUrl[];
  validationBypassed?: boolean;
  bypassReason?: "state" | "facility";
};

type ActivityErrorDisplayProps = {
  activity: Activity;
  facility?: Facility | null;
  tenant?: Tenant | null;
};

/**
 * Use this component to define a custom display for activity errors.
 *
 * For example, presubmission activities have issues as part of their
 * output so we have a custom display for those.
 */
export const ActivityErrorDisplay = ({
  activity,
  facility,
  tenant,
}: ActivityErrorDisplayProps) => {
  if (activity.type === ActivityType.Presubmission) {
    const output = activity.output as PresubmissionOutput | undefined;
    const issues = (output?.issues ?? []) as IssueWithUrl[];
    const validationBypassed = output?.validationBypassed ?? false;
    const bypassReason = output?.bypassReason;

    return (
      <Stack sx={{ flex: 1 }} gap={1}>
        {validationBypassed && (
          <Alert severity="info">
            Validation was bypassed for this {bypassReason}.
          </Alert>
        )}
        {issues.length > 0 && (
          <IssuesDataGrid
            issues={issues}
            validationBypassed={validationBypassed}
          />
        )}
      </Stack>
    );
  }

  if (
    activity.type === ActivityType.Error &&
    activity.description === CREDENTIAL_REQUIRED_ACTIVITY_DESCRIPTION
  ) {
    const facilityCredentialsUrl =
      facility?.id != null && tenant?.id != null
        ? `/o/${tenant.id}/facilities/${facility.id}/credentials`
        : null;

    return (
      <Box sx={{ flex: 1 }} component="section">
        <Typography variant="body1" sx={{ mb: 2 }}>
          The submission workflow for this report cannot be created because the
          facility needs to have a single Tier II credential.
        </Typography>
        <Stack component="ol" sx={{ pl: 2.5, m: 0 }} spacing={1}>
          <Typography component="li" variant="body2">
            {facilityCredentialsUrl != null ? (
              <Link
                href={facilityCredentialsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Update the Tier II credential(s) for this facility
              </Link>
            ) : null}
          </Typography>
          <Typography component="li" variant="body2">
            After ensuring the facility has a single Tier II credential, use{" "}
            <strong>Reset Report</strong> in the left sidebar and force cancel
            the workflow so that a new workflow can be created.
          </Typography>
        </Stack>
      </Box>
    );
  }

  return <></>;
};
